import { useEffect, useState } from 'react';

const BASE_URL = process.env.baseUrl;
const ACTIVE_BANNER_ENDPOINT = '/api/v1/campaign/active-banner';
const CACHE_KEY = 'homeBanner:v1';

const readCache = () => {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw == null) return undefined;
        return JSON.parse(raw);
    } catch (e) {
        return undefined;
    }
};

const writeCache = (banner) => {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ banner }));
    } catch (e) {
        // private mode / quota exceeded — banner stays in-memory only
    }
};

export const evictBannerCache = () => {
    try {
        sessionStorage.removeItem(CACHE_KEY);
    } catch (e) {
        // ignore
    }
};

const normalizeBanner = (payload) => {
    const b = payload && payload.banner;
    if (!b || typeof b !== 'object') return null;
    if (!b.slug || !b.text || !b.fallback_url) return null;
    return { slug: b.slug, text: b.text, fallback_url: b.fallback_url };
};

export const useActiveBanner = () => {
    const [state, setState] = useState(() => {
        const cached = readCache();
        return cached !== undefined
            ? { status: 'ready', banner: cached.banner }
            : { status: 'loading', banner: null };
    });

    useEffect(() => {
        if (state.status === 'ready') return undefined;

        // Snapshot the token once so a mid-flight logout can't race the header.
        const token = localStorage.getItem('t_id');
        if (!token) {
            // Anonymous: skip render; do NOT cache so a same-session login refetches.
            setState({ status: 'ready', banner: null });
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}${ACTIVE_BANNER_ENDPOINT}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json;charset=utf-8',
                        Authorization: `Bearer ${token}`,
                        'x-auth-token': `Bearer ${token}`,
                    },
                });
                if (!res.ok) throw new Error(`status ${res.status}`);
                const json = await res.json();
                const banner =
                    json && json.success ? normalizeBanner(json.payload) : null;
                if (cancelled) return;
                writeCache(banner);
                setState({ status: 'ready', banner });
            } catch (e) {
                if (cancelled) return;
                // Treat outage as "no banner" for this session. Don't write the
                // cache — next session should retry rather than carry a failure.
                setState({ status: 'ready', banner: null });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return state;
};
